<template>
  <v-card rounded="lg" elevation="0" class="pa-6 mb-4">
    <div class="d-flex align-center justify-space-between mb-2">
      <h3 class="text-h6 font-weight-bold mb-0">
        {{ $t('settings.whatsapp.botRules.title') }}
      </h3>
      <v-btn
        icon
        variant="text"
        size="small"
        :aria-label="$t('settings.whatsapp.botRules.orderHelp')"
      >
        <v-icon>mdi-help-circle-outline</v-icon>
        <v-tooltip activator="parent" location="bottom" max-width="360">
          {{ $t('settings.whatsapp.botRules.orderHelp') }}
        </v-tooltip>
      </v-btn>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-4">
      {{ $t('settings.whatsapp.botRules.lead') }}
    </p>

    <v-alert
      v-if="excludeContactsOn && contactsCount === 0"
      type="warning"
      variant="tonal"
      density="compact"
      class="mb-4"
    >
      {{ $t('settings.whatsapp.botRules.noSyncedContacts') }}
    </v-alert>

    <p class="text-caption text-medium-emphasis mb-4">
      {{ $t('settings.whatsapp.botRules.syncedContacts', { count: contactsCount }) }}
    </p>

    <v-switch
      v-model="form.exclude_contacts"
      color="red-darken-3"
      :label="$t('settings.whatsapp.botRules.excludeContacts')"
      :hint="$t('settings.whatsapp.botRules.excludeContactsHint')"
      persistent-hint
      class="mb-2"
    />
    <v-switch
      v-model="form.exclude_known_customers"
      color="red-darken-3"
      :label="$t('settings.whatsapp.botRules.excludeKnown')"
      :hint="$t('settings.whatsapp.botRules.excludeKnownHint')"
      persistent-hint
      class="mb-2"
    />
    <v-switch
      v-model="form.allowlist_mode"
      color="red-darken-3"
      :label="$t('settings.whatsapp.botRules.allowlistMode')"
      :hint="$t('settings.whatsapp.botRules.allowlistHint')"
      persistent-hint
      class="mb-4"
    />

    <v-text-field
      v-model.number="form.pause_after_staff_reply_minutes"
      type="number"
      min="0"
      max="1440"
      :label="$t('settings.whatsapp.botRules.pauseMinutes')"
      :hint="$t('settings.whatsapp.botRules.pauseMinutesHint')"
      persistent-hint
      variant="outlined"
      density="comfortable"
      class="mb-4"
    />

    <div class="d-flex flex-wrap gap-2 mb-4">
      <v-btn variant="outlined" color="red-darken-3" @click="openListDialog('excluded')">
        {{ $t('settings.whatsapp.botRules.excludedBtn', { n: excludedCount }) }}
      </v-btn>
      <v-btn variant="outlined" color="red-darken-3" @click="openListDialog('always_reply')">
        {{ $t('settings.whatsapp.botRules.alwaysBtn', { n: alwaysCount }) }}
      </v-btn>
      <v-btn
        v-if="legacyImport.length"
        variant="text"
        color="primary"
        :loading="importLoading"
        @click="importLegacy"
      >
        {{ $t('settings.whatsapp.botRules.importLegacy', { n: legacyImport.length }) }}
      </v-btn>
    </div>

    <v-btn
      color="red-darken-3"
      variant="flat"
      :loading="saving"
      :disabled="!flagsDirty"
      @click="saveFlags"
    >
      {{ $t('settings.whatsapp.botRules.save') }}
    </v-btn>

    <v-dialog
      v-model="listDialog.open"
      :max-width="listDialog.kind === 'excluded' ? 920 : 560"
      scrollable
    >
      <v-card class="pa-4">
        <v-card-title class="d-flex align-center flex-wrap gap-2">
          <span>
            {{ listDialog.kind === 'always_reply'
              ? $t('settings.whatsapp.botRules.alwaysTitle')
              : $t('settings.whatsapp.botRules.excludedTitle') }}
          </span>
          <v-spacer />
          <template v-if="listDialog.kind === 'excluded'">
            <v-btn
              variant="outlined"
              color="red-darken-3"
              size="small"
              prepend-icon="mdi-file-upload-outline"
              :loading="importPreviewLoading"
              @click="triggerImportFile"
            >
              {{ $t('settings.whatsapp.botRules.importFromFile') }}
            </v-btn>
            <v-btn
              variant="text"
              color="error"
              size="small"
              :disabled="!excludedList.total"
              @click="clearAllDialog = true"
            >
              {{ $t('settings.whatsapp.botRules.clearAll') }}
            </v-btn>
          </template>
        </v-card-title>

        <template v-if="listDialog.kind === 'excluded'">
          <div v-if="importPreview" class="mb-4">
            <p class="text-subtitle-2 mb-2">
              {{ $t('settings.whatsapp.botRules.importPreviewTitle') }}
            </p>
            <div class="d-flex flex-wrap gap-2 mb-3">
              <v-chip size="small" color="success" variant="tonal">
                {{ $t('settings.whatsapp.botRules.chipValid') }}: {{ importPreview.counts.valid }}
              </v-chip>
              <v-chip size="small" color="warning" variant="tonal">
                {{ $t('settings.whatsapp.botRules.chipDuplicates') }}:
                {{ importPreview.counts.duplicates }}
              </v-chip>
              <v-chip size="small" variant="tonal">
                {{ $t('settings.whatsapp.botRules.chipAlreadyExcluded') }}:
                {{ importPreview.counts.already_excluded }}
              </v-chip>
              <v-chip size="small" color="error" variant="tonal">
                {{ $t('settings.whatsapp.botRules.chipConflicts') }}:
                {{ importPreview.counts.conflicts }}
              </v-chip>
              <v-chip size="small" color="error" variant="outlined">
                {{ $t('settings.whatsapp.botRules.chipInvalid') }}:
                {{ importPreview.counts.invalid }}
              </v-chip>
            </div>
            <v-data-table
              :headers="importPreviewHeaders"
              :items="importPreview.rows"
              item-value="_id"
              density="compact"
              :items-per-page="10"
              class="mb-2 import-preview-table"
            >
              <template #item.raw="{ item }">
                <span class="text-body-2">{{ item.raw || '—' }}</span>
              </template>
              <template #item.normalized="{ item }">
                <span v-if="item.normalized">+{{ item.normalized }}</span>
                <span v-else>—</span>
              </template>
              <template #item.status="{ item }">
                <v-chip
                  size="x-small"
                  :color="importStatusColor(item.status)"
                  variant="tonal"
                >
                  {{ importStatusLabel(item.status) }}
                </v-chip>
                <p
                  v-if="importStatusHint(item.status)"
                  class="text-caption text-medium-emphasis mb-0 mt-1"
                >
                  {{ importStatusHint(item.status) }}
                </p>
              </template>
            </v-data-table>
            <v-alert type="warning" variant="tonal" density="compact" class="mb-3">
              {{ $t('settings.whatsapp.botRules.privacyPurgeNotice') }}
            </v-alert>
            <div class="d-flex flex-wrap gap-2 mb-4">
              <v-btn
                color="red-darken-3"
                variant="flat"
                :disabled="!importPreview.counts.valid"
                :loading="importConfirmLoading"
                @click="confirmFileImport"
              >
                {{
                  $t('settings.whatsapp.botRules.addImportCount', {
                    n: importPreview.counts.valid,
                  })
                }}
              </v-btn>
              <v-btn variant="text" @click="clearImportPreview">
                {{ $t('settings.whatsapp.botRules.cancelImportPreview') }}
              </v-btn>
            </div>
            <v-divider class="mb-4" />
          </div>

          <v-text-field
            v-model="excludedList.search"
            :label="$t('settings.whatsapp.botRules.searchExcluded')"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            class="mb-3"
            @keyup.enter="fetchExcludedPage(1)"
            @click:clear="onExcludedSearchClear"
          />

          <div class="d-flex align-center flex-wrap gap-2 mb-2">
            <v-btn
              variant="outlined"
              size="small"
              color="error"
              :disabled="!selectedExcluded.length"
              :loading="bulkRemoveLoading"
              @click="removeSelectedExcluded"
            >
              {{ $t('settings.whatsapp.botRules.removeSelected', { n: selectedExcluded.length }) }}
            </v-btn>
            <span class="text-caption text-medium-emphasis">
              {{ $t('settings.whatsapp.botRules.excludedListTotal', { n: excludedList.total }) }}
            </span>
          </div>

          <v-data-table
            v-model="selectedExcluded"
            :headers="excludedTableHeaders"
            :items="excludedList.items"
            item-value="number"
            show-select
            :loading="excludedList.loading"
            density="compact"
            :items-per-page="excludedList.limit"
            hide-default-footer
            class="mb-2"
          >
            <template #item.number="{ item }">
              +{{ item.number }}
            </template>
            <template #item.label="{ item }">
              {{ item.label || '—' }}
            </template>
            <template #item.source="{ item }">
              <span v-if="item.source === 'import'">
                {{ $t('settings.whatsapp.botRules.sourceImport') }}
              </span>
              <span v-else>—</span>
            </template>
            <template #item.actions="{ item }">
              <v-btn
                v-if="item.source === 'import' && item.import_id"
                size="x-small"
                variant="text"
                color="primary"
                class="mr-1"
                @click="confirmUndoImport(item.import_id)"
              >
                {{ $t('settings.whatsapp.botRules.undoImport') }}
              </v-btn>
              <v-btn
                icon
                size="small"
                variant="text"
                @click="confirmDelete(item.number)"
              >
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-pagination
            v-if="excludedList.total_pages > 1"
            v-model="excludedList.page"
            :length="excludedList.total_pages"
            density="compact"
            class="mb-4"
            @update:model-value="fetchExcludedPage"
          />
          <p class="text-caption text-medium-emphasis mb-2">
            {{ $t('settings.whatsapp.botRules.privacyPurgeOnAddHint') }}
          </p>
        </template>

        <template v-else>
          <v-table density="compact" class="mb-3">
            <thead>
              <tr>
                <th>{{ $t('settings.whatsapp.botRules.colNumber') }}</th>
                <th>{{ $t('settings.whatsapp.botRules.colLabel') }}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in listDialog.rows" :key="row.number">
                <td>+{{ row.number }}</td>
                <td>{{ row.label || '—' }}</td>
                <td class="text-right">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    @click="confirmDelete(row.number)"
                  >
                    <v-icon>mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </template>

        <v-row dense class="mt-1">
          <v-col cols="12" sm="5">
            <v-autocomplete
              v-model="listDialog.single.prefix"
              :items="dialPrefixItems"
              item-title="label"
              item-value="value"
              :label="$t('settings.whatsapp.botRules.prefixLabel')"
              variant="outlined"
              density="compact"
              auto-select-first
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="7">
            <v-text-field
              v-model="listDialog.single.localNumber"
              :label="$t('settings.whatsapp.botRules.addNumber')"
              :hint="$t('settings.whatsapp.botRules.addNumberHint')"
              persistent-hint
              variant="outlined"
              density="compact"
              inputmode="tel"
            />
          </v-col>
        </v-row>
        <p
          v-if="listDialogNumberPreview"
          class="text-caption text-medium-emphasis mb-2"
        >
          {{ $t('settings.whatsapp.botRules.normalizedPreview', { number: listDialogNumberPreview }) }}
        </p>
        <v-text-field
          v-model="listDialog.single.label"
          :label="$t('settings.whatsapp.botRules.addLabel')"
          variant="outlined"
          density="compact"
        />
        <v-textarea
          v-model="listDialog.paste"
          :label="$t('settings.whatsapp.botRules.pasteLabel')"
          :hint="$t('settings.whatsapp.botRules.pasteHint')"
          persistent-hint
          variant="outlined"
          rows="3"
          class="mt-2"
        />
        <v-card-actions class="px-0">
          <v-spacer />
          <v-btn variant="text" @click="closeListDialog">
            {{ $t('close') }}
          </v-btn>
          <v-btn color="red-darken-3" variant="flat" :loading="listDialog.loading" @click="addToList">
            {{ $t('settings.whatsapp.botRules.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <input
      ref="importFileInput"
      type="file"
      accept=".vcf,.csv,text/vcard,text/csv"
      class="d-none"
      @change="onImportFileSelected"
    >

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card class="pa-4">
        <v-card-title>{{ $t('settings.whatsapp.botRules.deleteConfirm') }}</v-card-title>
        <v-card-text>+{{ deleteNumber }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">{{ $t('close') }}</v-btn>
          <v-btn color="error" variant="flat" :loading="deleteLoading" @click="doDelete">
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="clearAllDialog" max-width="440">
      <v-card class="pa-4">
        <v-card-title>{{ $t('settings.whatsapp.botRules.clearAllConfirmTitle') }}</v-card-title>
        <v-card-text>{{ $t('settings.whatsapp.botRules.clearAllConfirmBody') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="clearAllDialog = false">{{ $t('close') }}</v-btn>
          <v-btn color="error" variant="flat" :loading="clearAllLoading" @click="doClearAllExcluded">
            {{ $t('settings.whatsapp.botRules.clearAll') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="undoImportDialog" max-width="440">
      <v-card class="pa-4">
        <v-card-title>{{ $t('settings.whatsapp.botRules.undoImportConfirmTitle') }}</v-card-title>
        <v-card-text>{{ $t('settings.whatsapp.botRules.undoImportConfirmBody') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="undoImportDialog = false">{{ $t('close') }}</v-btn>
          <v-btn color="error" variant="flat" :loading="undoImportLoading" @click="doUndoImport">
            {{ $t('settings.whatsapp.botRules.undoImport') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  getWhatsappBotRules,
  putWhatsappBotRules,
  addWhatsappBotRulesList,
  deleteWhatsappBotRulesList,
  getWhatsappExcludedList,
  previewWhatsappExcludedImport,
  confirmWhatsappExcludedImport,
  bulkRemoveWhatsappExcluded,
  deleteWhatsappExcludedNumber,
  clearWhatsappExcludedList,
  undoWhatsappExcludedImport,
} from '@/api/whatsapp';
import { whatsappErrorMessage } from '@/utils/whatsappErrors';
import {
  WA_DIAL_PREFIXES,
  DEFAULT_WA_DIAL_PREFIX,
  composeWaListNumber,
} from '@/utils/waDialPrefixes';

export default {
  name: 'WhatsappBotRules',
  props: {
    institutionId: { type: String, required: true },
    whatsapp: { type: Object, default: null },
  },
  emits: ['snackbar', 'reload'],
  data() {
    return {
      loading: true,
      saving: false,
      importLoading: false,
      botRules: null,
      contactsCount: 0,
      legacyImport: [],
      form: {
        allowlist_mode: false,
        exclude_contacts: false,
        exclude_known_customers: false,
        pause_after_staff_reply_minutes: 2,
      },
      baselineFlags: '',
      listDialog: {
        open: false,
        kind: 'excluded',
        rows: [],
        single: { prefix: DEFAULT_WA_DIAL_PREFIX, localNumber: '', label: '' },
        paste: '',
        loading: false,
      },
      deleteDialog: false,
      deleteNumber: '',
      deleteLoading: false,
      excludedList: {
        items: [],
        total: 0,
        page: 1,
        limit: 25,
        total_pages: 1,
        search: '',
        loading: false,
      },
      selectedExcluded: [],
      importPreview: null,
      importPreviewLoading: false,
      importConfirmLoading: false,
      bulkRemoveLoading: false,
      clearAllDialog: false,
      clearAllLoading: false,
      undoImportDialog: false,
      undoImportId: '',
      undoImportLoading: false,
    };
  },
  computed: {
    excludeContactsOn() {
      return Boolean(this.form.exclude_contacts);
    },
    excludedCount() {
      if (this.excludedList.total > 0) {
        return this.excludedList.total;
      }
      return (this.botRules && this.botRules.excluded && this.botRules.excluded.length) || 0;
    },
    alwaysCount() {
      return (this.botRules && this.botRules.always_reply && this.botRules.always_reply.length) || 0;
    },
    flagsDirty() {
      return this.baselineFlags !== JSON.stringify(this.form);
    },
    dialPrefixItems() {
      return WA_DIAL_PREFIXES;
    },
    listDialogNumberPreview() {
      const composed = composeWaListNumber(
        this.listDialog.single.prefix,
        this.listDialog.single.localNumber,
      );
      return composed || '';
    },
    excludedTableHeaders() {
      return [
        { title: this.$t('settings.whatsapp.botRules.colNumber'), key: 'number' },
        { title: this.$t('settings.whatsapp.botRules.colLabel'), key: 'label' },
        { title: this.$t('settings.whatsapp.botRules.colSource'), key: 'source', sortable: false },
        { title: '', key: 'actions', sortable: false, width: 140 },
      ];
    },
    importPreviewHeaders() {
      return [
        { title: this.$t('settings.whatsapp.botRules.colLabel'), key: 'name' },
        { title: this.$t('settings.whatsapp.botRules.colRaw'), key: 'raw' },
        { title: this.$t('settings.whatsapp.botRules.colNumber'), key: 'normalized' },
        { title: this.$t('settings.whatsapp.botRules.colStatus'), key: 'status' },
      ];
    },
  },
  watch: {
    institutionId: { immediate: true, handler: 'load' },
    whatsapp: {
      deep: true,
      handler(w) {
        if (w && w.contacts_count != null) {
          this.contactsCount = Number(w.contacts_count) || 0;
        }
      },
    },
  },
  methods: {
    async load() {
      if (!this.institutionId) return;
      this.loading = true;
      try {
        const data = await getWhatsappBotRules(this.institutionId);
        this.botRules = data.bot_rules;
        this.contactsCount = data.contacts_count != null ? data.contacts_count : 0;
        this.legacyImport = data.legacy_private_contacts_import || [];
        this.hydrateForm(data.bot_rules);
        try {
          const page = await getWhatsappExcludedList(this.institutionId, { page: 1, limit: 1 });
          this.excludedList.total = page.total != null ? page.total : 0;
        } catch (_) {
          /* paginated list optional until API is deployed */
        }
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.loading = false;
      }
    },
    hydrateForm(rules) {
      const r = rules || {};
      this.form = {
        allowlist_mode: Boolean(r.allowlist_mode),
        exclude_contacts: Boolean(r.exclude_contacts),
        exclude_known_customers: Boolean(r.exclude_known_customers),
        pause_after_staff_reply_minutes: r.pause_after_staff_reply_minutes != null
          ? Number(r.pause_after_staff_reply_minutes)
          : 2,
      };
      this.baselineFlags = JSON.stringify(this.form);
    },
    async saveFlags() {
      this.saving = true;
      try {
        const res = await putWhatsappBotRules(this.institutionId, this.form);
        this.botRules = res.bot_rules;
        this.hydrateForm(res.bot_rules);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.botRules.saved'),
          color: 'success',
        });
        this.$emit('reload');
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.saving = false;
      }
    },
    openListDialog(kind) {
      this.listDialog.kind = kind;
      this.listDialog.rows = [...((this.botRules && this.botRules[kind]) || [])];
      this.listDialog.single = {
        prefix: DEFAULT_WA_DIAL_PREFIX,
        localNumber: '',
        label: '',
      };
      this.listDialog.paste = '';
      this.selectedExcluded = [];
      this.clearImportPreview();
      if (kind === 'excluded') {
        this.excludedList.page = 1;
        this.excludedList.search = '';
        this.fetchExcludedPage(1);
      }
      this.listDialog.open = true;
    },
    closeListDialog() {
      this.listDialog.open = false;
      this.clearImportPreview();
    },
    async fetchExcludedPage(page) {
      if (page != null) {
        this.excludedList.page = page;
      }
      this.excludedList.loading = true;
      try {
        const data = await getWhatsappExcludedList(this.institutionId, {
          search: this.excludedList.search || undefined,
          page: this.excludedList.page,
          limit: this.excludedList.limit,
        });
        this.excludedList.items = data.items || [];
        this.excludedList.total = data.total != null ? data.total : 0;
        this.excludedList.total_pages = data.total_pages || 1;
        this.selectedExcluded = [];
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.excludedList.loading = false;
      }
    },
    onExcludedSearchClear() {
      this.excludedList.search = '';
      this.fetchExcludedPage(1);
    },
    triggerImportFile() {
      if (this.$refs.importFileInput) {
        this.$refs.importFileInput.value = '';
        this.$refs.importFileInput.click();
      }
    },
    async onImportFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.importPreviewLoading = true;
      try {
        const defaultCc = String(this.listDialog.single.prefix || '').replace(/\D/g, '');
        const data = await previewWhatsappExcludedImport(
          this.institutionId,
          file,
          defaultCc || undefined,
        );
        this.importPreview = {
          ...data,
          rows: (data.rows || []).map((row, index) => ({
            ...row,
            _id: `preview-${index}`,
          })),
        };
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.importPreviewLoading = false;
      }
    },
    clearImportPreview() {
      this.importPreview = null;
    },
    async confirmFileImport() {
      if (!this.importPreview || !this.importPreview.import_token) return;
      this.importConfirmLoading = true;
      try {
        const res = await confirmWhatsappExcludedImport(
          this.institutionId,
          this.importPreview.import_token,
        );
        this.clearImportPreview();
        await this.load();
        await this.fetchExcludedPage(1);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.botRules.importConfirmDone', {
            added: res.added,
            skipped: res.skipped,
            total: res.total_excluded,
          }),
          color: 'success',
        });
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.importConfirmLoading = false;
      }
    },
    importStatusColor(status) {
      const map = {
        valid: 'success',
        duplicate_in_file: 'warning',
        already_excluded: 'grey',
        in_always_reply: 'error',
        invalid: 'error',
      };
      return map[status] || 'grey';
    },
    importStatusLabel(status) {
      const key = `settings.whatsapp.botRules.importStatus.${status}`;
      const t = this.$t(key);
      return t === key ? status : t;
    },
    importStatusHint(status) {
      if (status === 'invalid' || status === 'in_always_reply') {
        return this.$t(`settings.whatsapp.botRules.importStatusHint.${status}`);
      }
      return '';
    },
    async removeSelectedExcluded() {
      if (!this.selectedExcluded.length) return;
      this.bulkRemoveLoading = true;
      try {
        const res = await bulkRemoveWhatsappExcluded(
          this.institutionId,
          [...this.selectedExcluded],
        );
        await this.load();
        await this.fetchExcludedPage(this.excludedList.page);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.botRules.bulkRemoved', { n: res.removed }),
          color: 'success',
        });
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.bulkRemoveLoading = false;
      }
    },
    confirmUndoImport(importId) {
      this.undoImportId = importId;
      this.undoImportDialog = true;
    },
    async doUndoImport() {
      if (!this.undoImportId) return;
      this.undoImportLoading = true;
      try {
        const res = await undoWhatsappExcludedImport(this.institutionId, this.undoImportId);
        this.undoImportDialog = false;
        this.undoImportId = '';
        await this.load();
        await this.fetchExcludedPage(this.excludedList.page);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.botRules.undoImportDone', { n: res.removed }),
          color: 'success',
        });
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.undoImportLoading = false;
      }
    },
    async doClearAllExcluded() {
      this.clearAllLoading = true;
      try {
        const res = await clearWhatsappExcludedList(this.institutionId);
        this.clearAllDialog = false;
        await this.load();
        await this.fetchExcludedPage(1);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.botRules.clearAllDone', { n: res.removed }),
          color: 'success',
        });
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.clearAllLoading = false;
      }
    },
    async addToList() {
      this.listDialog.loading = true;
      try {
        const payload = { paste: this.listDialog.paste };
        const composed = composeWaListNumber(
          this.listDialog.single.prefix,
          this.listDialog.single.localNumber,
        );
        if (composed) {
          payload.entries = [{
            number: composed,
            label: this.listDialog.single.label.trim(),
          }];
        }
        const res = await addWhatsappBotRulesList(
          this.institutionId,
          this.listDialog.kind,
          payload,
        );
        this.botRules = res.bot_rules;
        if (this.listDialog.kind === 'excluded') {
          await this.fetchExcludedPage(this.excludedList.page);
        } else {
          this.listDialog.rows = [...(res.bot_rules[this.listDialog.kind] || [])];
        }
        this.listDialog.paste = '';
        this.listDialog.single = {
          prefix: DEFAULT_WA_DIAL_PREFIX,
          localNumber: '',
          label: '',
        };
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.botRules.listUpdated'),
          color: 'success',
        });
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.listDialog.loading = false;
      }
    },
    confirmDelete(number) {
      this.deleteNumber = number;
      this.deleteDialog = true;
    },
    async doDelete() {
      this.deleteLoading = true;
      try {
        let res;
        if (this.listDialog.kind === 'excluded') {
          res = await deleteWhatsappExcludedNumber(this.institutionId, this.deleteNumber);
        } else {
          res = await deleteWhatsappBotRulesList(
            this.institutionId,
            this.listDialog.kind,
            { number: this.deleteNumber },
          );
        }
        this.botRules = res.bot_rules;
        if (this.listDialog.kind === 'excluded') {
          await this.load();
          await this.fetchExcludedPage(this.excludedList.page);
        } else {
          this.listDialog.rows = [...(res.bot_rules[this.listDialog.kind] || [])];
        }
        this.deleteDialog = false;
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.deleteLoading = false;
      }
    },
    async importLegacy() {
      this.importLoading = true;
      try {
        const entries = this.legacyImport.map((e) => ({
          number: e.number,
          label: e.label || '',
        }));
        const res = await addWhatsappBotRulesList(this.institutionId, 'excluded', { entries });
        this.botRules = res.bot_rules;
        this.legacyImport = [];
        this.excludedList.page = 1;
        await this.fetchExcludedPage(1);
        this.$emit('snackbar', {
          message: this.$t('settings.whatsapp.botRules.importDone'),
          color: 'success',
        });
      } catch (err) {
        this.$emit('snackbar', { message: whatsappErrorMessage(err), color: 'error' });
      } finally {
        this.importLoading = false;
      }
    },
  },
};
</script>
